import {
  ChatAvailabilityResult,
  ChatEventType,
  CustomerChatMessage,
} from '@varo/graphql-schema-types';
import ChatBubble from 'features/chat/ChatBubble/ChatBubble';
import {
  getMergedQueuePosition,
  getMsgState,
  handleChatEvent,
} from 'features/chat/helpers/ChatEvent.helper';
import { getLatestBannerMsg } from 'features/chat/helpers/StatusBanner.helper';
import HistoryBanner from 'features/chat/HistoryBanner/HistoryBanner';
import useAppVisibility from 'features/chat/hooks/useAppVisibility';
import useReadReceipt from 'features/chat/hooks/useReadReceipt';
import MessageItem from 'features/chat/MessageItem/MessageItem';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import StatusBanner from 'features/chat/StatusBanner/StatusBanner';
import TypingIndicator from 'features/chat/TypingIndicator/TypingIndicator';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { FlatList, StyleSheet, TouchableOpacity, View } from 'react-native';
import ChatEvent from 'services/graphql/subscriptions/ChatEvent.subscription';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import theme from 'shared/styles/theme';
import { logError } from 'shared/utils/sentry/sentry';
import { CombinedError, useSubscription } from 'urql';

export interface MessageListProps {
  messages: CustomerChatMessage[];
  historyConnectionFetching: boolean;
  historyFetchingError?: CombinedError;
  fetchHistory: () => void;
  chatAvailability?: ChatAvailabilityResult;
  reExecuteAvailability: () => void;
}

const MessageList = ({
  messages,
  historyConnectionFetching,
  historyFetchingError,
  fetchHistory,
  chatAvailability,
  reExecuteAvailability,
}: MessageListProps) => {
  const flatListRef = useRef<FlatList<CustomerChatMessage>>(null);
  const cleanupFunctionsRef = useRef<Array<() => void>>([]);
  const {
    chatOutageMessage,
    setHistory,
    queuePosition,
    setQueuePosition,
    isAgentTyping,
    setIsAgentTyping,
    chatSessionId,
    setChatSessionId,
    isChattingWithBot,
    setIsChattingWithBot,
    setIsSessionActive,
    isChatStarted,
    setIsChatStarted,
    setIsChatEndedFromEvent,
    startBotChat,
  } = useContext(ChatContext);

  const { ackChatMsg } = useReadReceipt();
  const isHidden = useAppVisibility();
  const processedEventsCountRef = useRef(0);

  useEffect(() => {
    return () => {
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
      cleanupFunctionsRef.current = [];
    };
  }, []);

  const [{ data: subscriptionEvents, error }] = useSubscription<
    { onChatEvent: CustomerChatMessage },
    CustomerChatMessage[]
  >(
    {
      query: ChatEvent,
    },
    (prevEvents, response) => {
      const currentEvents = prevEvents ?? [];
      const event = response.onChatEvent;

      if (!event) {
        return currentEvents;
      }

      if (currentEvents.some((item) => item.id === event.id)) {
        return currentEvents;
      }

      return [...currentEvents, event].slice(-100);
    },
  );

  const processEvent = useCallback(
    (event: CustomerChatMessage) => {
      const cleanup = handleChatEvent({
        chatEvent: event,
        getAvailabilityInfo: reExecuteAvailability,
        setIsAgentTyping,
        setIsChattingWithBot,
        setChatSessionId,
        startBotChat,
        setHistory,
        setIsSessionActive,
        setQueuePosition,
        setIsChatStarted,
        setIsChatEndedFromEvent,
      });
      cleanupFunctionsRef.current.push(cleanup);

      if (event.eventType === ChatEventType.Message && !isHidden) {
        ackChatMsg(event.id);
      }
    },
    [
      ackChatMsg,
      isHidden,
      reExecuteAvailability,
      setHistory,
      setIsAgentTyping,
      setIsChattingWithBot,
      setChatSessionId,
      startBotChat,
      setIsSessionActive,
      setQueuePosition,
      setIsChatStarted,
      setIsChatEndedFromEvent,
    ],
  );

  // Handle subscription events in useEffect to avoid setState during render
  useEffect(() => {
    if (!subscriptionEvents?.length) {
      return;
    }

    for (
      let index = processedEventsCountRef.current;
      index < subscriptionEvents.length;
      index += 1
    ) {
      const event = subscriptionEvents[index];
      if (event) {
        processEvent(event);
      }
    }

    processedEventsCountRef.current = subscriptionEvents.length;
  }, [processEvent, subscriptionEvents]);

  useEffect(() => {
    if (error?.response && error.message) {
      logError(`Error ${error.message}:${error.response} `);
    }
  }, [error]);

  useEffect(() => {
    if (queuePosition) {
      setHistory((history) => getMergedQueuePosition(history, queuePosition));
    }
  }, [queuePosition, setHistory]);

  return (
    <View style={styles.messageList}>
      {historyConnectionFetching ? (
        <View style={styles.loader}>
          <LoadingSpinner />
        </View>
      ) : null}
      {/* Display chat unavailable banner */}
      {!historyConnectionFetching && historyFetchingError ? (
        <StatusBanner
          unableToConnect={true}
          chatAvailability={chatAvailability}
        />
      ) : null}

      {!isChatStarted &&
      !historyConnectionFetching &&
      !historyFetchingError &&
      messages.length === 0 ? (
        <View style={styles.welcomeBanner}>
          {/* Display welcome banner */}
          <HistoryBanner
            unableToConnect={false}
            chatOutageMessage={chatOutageMessage}
            isLastItem={true}
            chatAvailability={chatAvailability}
          />
        </View>
      ) : null}

      <FlatList
        ListHeaderComponent={
          messages.length && !isChatStarted ? (
            <View>
              {/* Display start chat banner */}
              <HistoryBanner
                chatAvailability={chatAvailability}
                unableToConnect={false}
                chatOutageMessage={chatOutageMessage}
                isLastItem={true}
              />
            </View>
          ) : null
        }
        onEndReached={fetchHistory}
        ref={flatListRef}
        data={messages}
        renderItem={({ item, index }) => (
          <TouchableOpacity activeOpacity={1}>
            <MessageItem
              {...item}
              chatAvailability={chatAvailability}
              isLatestBanner={item.id === getLatestBannerMsg(messages)?.id}
              index={index}
              isDisabled={getMsgState(
                item,
                index,
                chatSessionId,
                isChattingWithBot,
              )}
            />
          </TouchableOpacity>
        )}
        /* Use stable unique keys to prevent Fabric mounting errors */
        keyExtractor={(item) => item.id}
        inverted
        removeClippedSubviews={false}
      />
      {isAgentTyping ? (
        <ChatBubble
          isTyping={true}
          position="left"
          createdAt={new Date().toJSON()}
          isDisabled={false}
          hideTimeStamp={true}
        >
          <TypingIndicator />
        </ChatBubble>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  loader: {
    alignItems: 'center',
  },
  messageList: {
    flex: 1,
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
    scrollBehavior: 'smooth',
  },
  welcomeBanner: {
    flex: 1,
  },
});

export default MessageList;
